import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  keyframes
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  BugReport as BugReportIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  ExitToApp as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Backup as BackupIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getUserRoleName, isAdmin, isSuperAdmin, hasRole } from '../../utils/userUtils';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import NotificationBell from '../NotificationBell';
import ConnectionStatus from '../ConnectionStatus';
import { useThemeColors } from '../../utils/themeColors';
import RealTimeNotification from '../RealTimeNotification';
import ProductUpdatePopup from '../ProductUpdatePopup';
import TourTrigger from '../tour/TourTrigger';
import MainLayoutTour from '../tour/MainLayoutTour';
import api from '../../utils/api';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

// Pulse animation for loading placeholders
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Get saved preference from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const defaultWhitelabelConfig = {
    companyName: 'Pentest Portal',
    dashboardTitle: 'Pentest Portal'
  };
  
  // SIMPLIFIED: Always use defaults, only update if API succeeds
  const [whitelabelConfigState, setWhitelabelConfigState] = useState<{
    companyName: string;
    dashboardTitle: string;
  }>(defaultWhitelabelConfig);
  const [configLoading, setConfigLoading] = useState(false);
  
  // ALWAYS return a valid config - never undefined
  const whitelabelConfig = useMemo(() => {
    // Validate and ensure we always have valid values
    const config = whitelabelConfigState || defaultWhitelabelConfig;
    return {
      companyName: config.companyName || defaultWhitelabelConfig.companyName,
      dashboardTitle: config.dashboardTitle || defaultWhitelabelConfig.dashboardTitle
    };
  }, [whitelabelConfigState]);
  
  // Safe alias
  const safeWhitelabelConfig = whitelabelConfig;
  
  const { user, logout } = useAuth();
  const { permissions, loading } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleTheme } = useCustomTheme();

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  // Load whitelabel configuration
  useEffect(() => {
    let isMounted = true;
    
    const loadWhitelabelConfig = async () => {
      if (configLoading) return; // Prevent multiple simultaneous requests
      
      try {
        setConfigLoading(true);
        const response = await api.get('/dashboard/whitelabel-config');
        
        if (!isMounted) return; // Component unmounted, don't update state
        
        // Ensure we always have a valid config object
        const config = response.data?.config || {};
        const newConfig = {
          companyName: config?.companyName || defaultWhitelabelConfig.companyName,
          dashboardTitle: config?.dashboardTitle || config?.companyName || defaultWhitelabelConfig.dashboardTitle
        };
        
        // Only update if we have valid data
        if (newConfig.companyName && newConfig.dashboardTitle) {
          setWhitelabelConfigState(newConfig);
          document.title = newConfig.dashboardTitle;
        } else {
          // Fallback to defaults if config is invalid
          setWhitelabelConfigState(defaultWhitelabelConfig);
          document.title = defaultWhitelabelConfig.dashboardTitle;
        }
      } catch (err) {
        if (!isMounted) return; // Component unmounted, don't update state
        
        console.error('Failed to load whitelabel config:', err);
        // Use defaults if API fails - ensure config is never undefined
        setWhitelabelConfigState(defaultWhitelabelConfig);
        document.title = defaultWhitelabelConfig.dashboardTitle;
      } finally {
        if (isMounted) {
          setConfigLoading(false);
        }
      }
    };

    // Only load config once when component mounts
    if (user && whitelabelConfig.companyName === defaultWhitelabelConfig.companyName) {
      loadWhitelabelConfig();
    }
    
    return () => {
      isMounted = false;
    };
  }, [user]); // Only depend on user authentication status

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Sidebar toggle (Ctrl+B or Cmd+B)
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        handleSidebarToggle();
      }
      // Theme toggle (Ctrl+T or Cmd+T)
      if ((event.ctrlKey || event.metaKey) && event.key === 't') {
        event.preventDefault();
        toggleTheme();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarCollapsed, isMobile, toggleTheme]);

  // Debug: Log permissions for troubleshooting
  
  
  // Define navigation groups
  const navigationGroups = {
    main: {
      title: 'Main',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', permission: null },
        {
          text: 'Engagements',
          icon: <BusinessIcon />,
          path: '/engagements',
          permission: loading ? undefined : permissions?.projects?.read
        },
        { 
          text: 'Findings', 
          icon: <BugReportIcon />, 
          path: '/findings', 
          permission: loading ? undefined : (permissions?.findings?.read || permissions?.findings?.write || permissions?.findings?.delete) 
        },
        { 
          text: 'Calendar', 
          icon: <CalendarIcon />, 
          path: '/calendar', 
          permission: true // Everyone can access calendar
        }
      ]
    },
    tools: {
      title: 'Tools',
      items: [
        { 
          text: 'Word Templates', 
          icon: <DescriptionIcon />, 
          path: '/word-templates', 
          permission: loading ? undefined : (permissions?.templates?.read && !hasRole(user, 'Project Stakeholder')) 
        },
        { 
          text: 'Report Library', 
          icon: <AssessmentIcon />, 
          path: '/report-library', 
          permission: true // Everyone can access report library
        },
        { 
          text: 'Methodology', 
          icon: <SecurityIcon />, 
          path: '/methodology', 
          permission: true // Everyone can view methodologies
        }
      ]
    },
    system: {
      title: 'System',
      items: [
        { 
          text: 'Admin', 
          icon: <AdminPanelSettingsIcon />, 
          path: '/admin', 
          permission: loading ? undefined : permissions?.admin?.read 
        }
      ]
    }
  };

  // Flatten all navigation items for backward compatibility
  const navigationItems = Object.values(navigationGroups).flatMap(group => group.items);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    // Only allow collapse on desktop
    if (!isMobile) {
      const newCollapsed = !sidebarCollapsed;
      setSidebarCollapsed(newCollapsed);
      localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileSettings = () => {
    navigate('/profile');
    setAnchorEl(null);
  };



  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box>
      <Box sx={{ 
        p: 2, 
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {!sidebarCollapsed && (
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
            {configLoading ? (
              <Box sx={{ width: 120, height: 24, bgcolor: 'action.hover', borderRadius: 1, animation: `${pulse} 1.5s ease-in-out infinite` }} />
            ) : (
              safeWhitelabelConfig.companyName
            )}
          </Typography>
        )}
        {!isMobile && (
          <Tooltip title={`${sidebarCollapsed ? 'Expand' : 'Collapse'} sidebar (Ctrl+B)`}>
            <IconButton
              onClick={handleSidebarToggle}
              sx={{ 
                ml: sidebarCollapsed ? 'auto' : 0,
                mr: sidebarCollapsed ? 'auto' : 0
              }}
            >
              {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider />
      
      {/* Render navigation groups */}
      {Object.entries(navigationGroups).map(([groupKey, group]) => {
        // Filter items in this group based on permissions
        const filteredGroupItems = group.items.filter(item => {
          if (item.permission === null) return true;
          if (item.permission === undefined) return false;
          return item.permission === true;
        });

        // Don't render group if no items are visible
        if (filteredGroupItems.length === 0) return null;

        return (
          <Box key={groupKey}>
            {/* Group Header */}
            {!sidebarCollapsed && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.75rem'
                  }}
                >
                  {group.title}
                </Typography>
              </Box>
            )}
            
            {/* Group Items */}
            <List sx={{ padding: 0 }}>
              {filteredGroupItems.map((item) => (
                <ListItem
                  button
                  key={item.text}
                  onClick={() => handleNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                      },
                    },
                    minHeight: 48,
                    px: sidebarCollapsed && !isMobile ? 1 : 2,
                    py: 0.5,
                    justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'flex-start',
                    '& .MuiListItemIcon-root': {
                      minWidth: sidebarCollapsed && !isMobile ? 'auto' : 40,
                      margin: 0,
                    },
                    '& .MuiListItemText-root': {
                      margin: 0,
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      },
                    },
                  }}
                >
                  <Tooltip 
                    title={sidebarCollapsed && !isMobile ? item.text : ''} 
                    placement="right"
                    disableHoverListener={!sidebarCollapsed || isMobile}
                  >
                    <ListItemIcon sx={{ 
                      color: 'inherit',
                      minWidth: sidebarCollapsed && !isMobile ? 0 : 40,
                      justifyContent: 'center',
                      margin: 0,
                      padding: 0,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                  </Tooltip>
                  {(!sidebarCollapsed || isMobile) && <ListItemText primary={item.text} />}
                </ListItem>
              ))}
            </List>
            
            {/* Add divider between groups (except for the last group) */}
            {groupKey !== 'system' && (
              <Divider sx={{ mx: 2, my: 1 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {configLoading ? (
              <Box sx={{ width: 140, height: 24, bgcolor: 'action.hover', borderRadius: 1, animation: `${pulse} 1.5s ease-in-out infinite` }} />
            ) : (
              safeWhitelabelConfig.companyName
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Connection Status */}
            <ConnectionStatus showDetails={false} />
            
            {/* Theme Toggle Button */}
            <Tooltip title="Toggle Theme (Ctrl+T)">
              <IconButton
                size="large"
                aria-label="toggle theme"
                onClick={toggleTheme}
                color="inherit"
              >
                {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>



            {/* Product Tour Button */}
            <TourTrigger variant="icon" size="large" color="inherit" />

            {/* Notifications Bell */}
            <NotificationBell data-testid="notification-bell" />

            {/* User Profile Button */}
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
              data-testid="user-menu"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Welcome, {user?.username || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.userRole?.name ? user.userRole.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'User'}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfileSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              {user?.is_super_admin && (
                <MenuItem onClick={() => navigate('/super-admin/dashboard')}>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Super Admin Portal
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
        
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          mt: '64px', // AppBar height
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>

      {/* Real-time notifications */}
      <RealTimeNotification />
      
                                                                               {/* Product Updates Popup */}
      {user && user.tenant_id && (
        <ProductUpdatePopup 
          userId={user.id} 
          tenantId={user.tenant_id} 
        />
      )}

      {/* Product Tour */}
      <MainLayoutTour />
    </Box>
  );
};

export default Layout; 